import React, { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

// Presentation only: closing a notice never releases a Design decision claim.
export default function DesignFeedback({ readState, acquired, directoryState, pending,
  refreshOrders, refreshDesigners, review, acknowledge, isCurrent, automaticActions }) {
  const scope = `design-feedback-${useId()}`;
  const sequence = useRef(0);
  const activeToast = useRef(null);
  const ownedToasts = useRef(new Set());
  const live = useRef(false);
  const latest = useRef(null);
  const [reopen, setReopen] = useState(0);
  const issues = [];
  const accepted = pending.filter(item => item.state === 'accepted');
  const unknown = pending.some(item => item.state === 'unknown');
  if (readState === 'unavailable' && !accepted.length && !unknown) {
    issues.push({ key: 'orders', message: acquired
      ? 'Orders could not be refreshed. Showing last-known rows.'
      : 'Orders unavailable; no current list established.',
    actions: [{ label: 'Retry orders', run: refreshOrders }] });
  }
  if (directoryState === 'unavailable') issues.push({ key: 'directory',
    message: 'Designer directory unavailable.', actions: [{ label: 'Retry designers', run: refreshDesigners }] });
  for (const item of pending) {
    if (item.state === 'pending' && !item.stalled) continue;
    if (item.state === 'accepted' && (readState === 'loading' || automaticActions?.has(item.id))) continue;
    const actions = item.state === 'accepted' ? [{ label: 'Retry refresh', run: refreshOrders }]
      : item.state === 'unknown' ? [{ label: 'Check current order', run: () => review(item.id) }] : [];
    if (item.canAcknowledge) actions.push({ label: 'Acknowledge and refresh', run: () => acknowledge(item.id) });
    issues.push({ key: `order-${item.id}`, message: item.state === 'accepted'
      ? `${item.message}. ${readState === 'unavailable' ? 'The order list could not be updated.' : 'The order list needs updating.'}`
      : item.state === 'unknown' ? 'Design action outcome unknown.'
        : 'Still waiting for the submitted action. Do not repeat it.',
    detail: item.review, actions });
  }
  // Callback changes do not republish a dismissed notice. Changed evidence does.
  const signature = JSON.stringify(issues.map(({ key, message, detail, actions }) =>
    [key, message, detail, actions.map(action => action.label)]));
  latest.current = { issues, isCurrent, signature };
  useEffect(() => {
    live.current = true;
    const owned = ownedToasts.current;
    return () => {
      live.current = false; activeToast.current = null;
      owned.forEach(id => toast.dismiss(id)); owned.clear();
    };
  }, []);
  useEffect(() => {
    if (!latest.current.issues.length) {
      if (activeToast.current) {
        toast.dismiss(activeToast.current); ownedToasts.current.delete(activeToast.current);
      }
      activeToast.current = null;
      return;
    }
    // Sonner removes dismissed notices after its exit animation. A reopened
    // notice needs a fresh identity so that old removal cannot erase recovery.
    const id = activeToast.current || `${scope}-${++sequence.current}`;
    activeToast.current = id; ownedToasts.current.add(id);
    const invoke = (key, label) => {
      const state = latest.current;
      if (!live.current || activeToast.current !== id || !state.isCurrent() || state.signature !== signature) return;
      const action = state.issues.find(issue => issue.key === key)?.actions.find(item => item.label === label);
      if (action) void action.run();
    };
    toast.error('Design needs attention', {
      id, duration: Infinity, closeButton: true,
      onDismiss: () => {
        if (activeToast.current === id) activeToast.current = null;
        ownedToasts.current.delete(id);
      },
      description: <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="design-recovery">
        {latest.current.issues.map(issue => <div key={issue.key} className="space-y-1">
          {issue.key.startsWith('order-') && <span className="text-xs">Order {issue.key.slice(6)}</span>}
          <p>{issue.message}</p>
          {issue.detail && <p>{issue.detail}</p>}
          <div className="flex flex-wrap gap-2">{issue.actions.map(action =>
            <Button key={action.label} size="sm" onClick={() => invoke(issue.key, action.label)}>{action.label}</Button>)}</div>
        </div>)}
      </div>,
    });
  }, [scope, signature, reopen]);
  const checking = readState === 'loading' || directoryState === 'loading';
  const saving = pending.some(item => item.state === 'pending');
  const label = issues.length ? 'Needs attention' : checking ? 'Checking…' : saving ? 'Saving…' : '';
  return <div className="w-28 h-7 flex-shrink-0" data-testid="design-feedback-slot">
    {issues.length ? <Button size="sm" variant="ghost" className="w-full h-full px-1 text-xs"
      onClick={() => setReopen(value => value + 1)}>Needs attention</Button>
      : <span role="status" className="flex h-full items-center justify-center text-xs text-stone-500">{label}</span>}
  </div>;
}
